import { useEffect, useState } from 'react';

import { faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import CaseDetails from './components/CaseDetails';
import UploadTypeSwitch from './components/UploadTypeSwitch';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useProcessCase } from '@/api/mutations/useProcessCase';
import { useSaveCase } from '@/api/mutations/useSaveCase';
import { useGetCase } from '@/api/queries/useGetCase';
import { FullCaseForm, fullCaseSchema, lightCaseSchema } from '@/api/schemas/case';
import { CaseCreationRequestDetails } from '@/api/types/mutations';
import { Form } from '@/components/molecules/form';
import { CaseCreationOnboardingModal } from '@/components/molecules/Modals/Onboarding/CaseCreationOnboardingModal';
import { DocumentUploadOnboardingModal } from '@/components/molecules/Modals/Onboarding/DocumentUploadOnboardingModal';
import PermittedFilesChip from '@/components/molecules/PermittedFilesChip';
import UploadHistoryChip from '@/components/molecules/UploadHistoryChip';
import FileUploaderComponent from '@/components/organisms/DocumentAdder';
import { parseURLParams } from '@/helpers/parseURLParams';
import { useJobProgress } from '@/hooks/useJobProgress';
import { UploadMode } from '@/types';

const CaseCreator = () => {
  const [uploadDisabled, setUploadDisabled] = useState(true);
  const [totalUnprocessedDocs, setTotalUnprocessedDocs] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = parseURLParams<string>(searchParams, 'caseId', '');
  const mode = parseURLParams<UploadMode>(searchParams, 'mode', UploadMode.Full);

  // Track active jobs for zip extraction
  const { activeJobs } = useJobProgress(caseId);

  const navigate = useNavigate();
  const { state } = useLocation();
  const fromChecklistDocumentUpload = state?.fromChecklistDocumentUpload;
  const fromChecklistCaseCreation = state?.fromChecklistCaseCreation;

  // Queries
  const { data: responseCase, isLoading: isLoadingCase } = useGetCase(caseId);
  const { mutate: saveCase } = useSaveCase(caseId || '');
  const { mutate: processCase, isLoading: isProcessingCase } = useProcessCase();

  const handleClickBack = () => {
    saveCase(
      {
        ...form.getValues(),
      },
      {
        onSuccess: () => {
          toast.success('Case saved.', {
            richColors: true,
            position: 'top-right',
            duration: 1500,
          });
          navigate(`/app/chronos/explore?matterId=${responseCase?.matterId}`);
        },
        onError: () => {
          toast.error('Failed to save case', {
            richColors: true,
            duration: 1500,
            position: 'top-right',
          });
          navigate(`/app/chronos/explore?matterId=${responseCase?.matterId}`);
        },
      },
    );
  };

  const form = useForm<FullCaseForm>({
    resolver: zodResolver(mode === UploadMode.Light ? lightCaseSchema : fullCaseSchema),
    defaultValues: {
      name: '',
      legalIssues: '',
      type: '',
      parties: '',
      keyContext: '',
      keyTimePeriod: '',
      disputeStatus: '',
      billable: true,
    },
  });

  const onSubmit = (data: Omit<CaseCreationRequestDetails, 'caseId'>) => {
    trackCustomEvent(TrackingEvents.CaseUploaded, { caseId, mode });
    processCase(
      {
        caseId,
        ...data,
      },
      {
        onError: () => {
          toast.error('Failed to process case', {
            duration: 3000,
          });
        },
        onSuccess: () => {
          toast.info('Processing your case', {
            duration: 7000,
            description:
              'We will calculate how many pages you have uploaded and provide a time estimate in a few minutes after we have read your documents.',
          });
          navigate(`/app/chronos/case-editor/summary?caseId=${caseId}`, {
            state: { fromChecklistCaseCreation, fromChecklistDocumentUpload },
          });
        },
      },
    );
  };

  // Load in any existing case metadata
  useEffect(() => {
    if (responseCase) {
      form.reset({
        name: responseCase?.name ?? '',
        legalIssues: responseCase?.legalIssues ?? '',
        parties: responseCase?.parties ?? '',
        keyTimePeriod: responseCase?.keyTimePeriod ?? '',
        keyContext: responseCase?.keyContext ?? '',
        disputeStatus: responseCase?.disputeStatus ?? '',
        type: responseCase?.type ?? '',
        billable: responseCase?.billable ?? true,
      });
    }
  }, [responseCase, form]);

  return (
    <div className="relative flex h-full w-full flex-col items-start justify-start overflow-hidden bg-gray-50 px-6 py-4">
      {!isLoadingCase && (
        <>
          <div className="flex h-full min-h-0 w-full flex-row gap-8">
            <div className="flex w-1/2 flex-col justify-between gap-2 overflow-auto rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <CaseDetails
                    mode={mode}
                    form={form}
                    onSubmit={onSubmit}
                    uploadDisabled={uploadDisabled}
                    handleClickBack={handleClickBack}
                    isProcessingCase={isProcessingCase}
                    activeJobs={activeJobs}
                  />
                </form>
              </Form>
              <div className="flex items-center justify-start">
                <UploadTypeSwitch mode={mode} setSearchParams={setSearchParams} />
              </div>
            </div>

            <div className="flex w-1/2 flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faFile} className="mr-3 h-4 w-4 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Files{' '}
                    {totalUnprocessedDocs ? <span className="ml-1 text-sm">{`(${totalUnprocessedDocs})`}</span> : ''}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <UploadHistoryChip caseId={caseId} />
                  <PermittedFilesChip />
                </div>
              </div>

              <FileUploaderComponent
                setUploadDisabled={setUploadDisabled}
                setTotalUnprocessedDocs={setTotalUnprocessedDocs}
                caseId={caseId ?? ''}
                isCaseCreator={true}
                activeJobs={activeJobs}
              />
            </div>
          </div>
        </>
      )}
      {fromChecklistDocumentUpload && <DocumentUploadOnboardingModal />}
      {fromChecklistCaseCreation && <CaseCreationOnboardingModal />}
    </div>
  );
};

export default CaseCreator;
